import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Send, Handshake } from "lucide-react";
import { PageHero, Reveal } from "../components/site/Motion";
import { FileUploader } from "../components/site/FileUploader";
import { AssetMultiSelect } from "../components/site/AssetMultiSelect";
import { http, apiError } from "../lib/api";

export const OPP_CATEGORIES = ["Business Partnership", "Contract Opportunity", "Project Proposal", "Supplier or Vendor Partnership",
  "Dealer or Broker Partnership", "Asset Acquisition or Procurement Opportunity", "Fleet or Equipment Sourcing Opportunity",
  "Joint Venture or Strategic Collaboration", "General Business Inquiry", "Other Business Opportunity"];

const INTRO = [
  "At Selby Asset Group, we believe that meaningful business relationships create opportunities for growth, collaboration, and long-term success.",
  "We welcome inquiries from businesses, organizations, suppliers, brokers, contractors, asset owners, and individuals interested in exploring opportunities to work with us.",
  "Whether you have a business partnership proposal, a contract opportunity, a project requiring our involvement, or an idea for future collaboration, we welcome the opportunity to learn more.",
  "Tell us about your business, your project, or the opportunity you would like to discuss. Our team will review your submission and determine the appropriate next steps.",
];

const EMPTY = { name: "", company: "", email: "", phone: "", website_url: "", address: "", category: "", division: "", industry: "", subject: "",
  description: "", requested_action: "", value: "", timeline: "", comments: "", website: "" };

const In = ({ id, label, req, f, set, type = "text", ph = "" }) => (
  <div>
    <label className="label" htmlFor={`op-${id}`}>{label}{req ? " *" : <span className="text-slate-400 normal-case tracking-normal"> (optional)</span>}</label>
    <input id={`op-${id}`} type={type} required={req} className="field" placeholder={ph} value={f[id]} onChange={set(id)} data-testid={`opp-${id}-input`} />
  </div>
);

const Area = ({ id, label, req, f, set, rows = 4, ph = "" }) => (
  <div className="sm:col-span-2">
    <label className="label" htmlFor={`op-${id}`}>{label}{req ? " *" : <span className="text-slate-400 normal-case tracking-normal"> (optional)</span>}</label>
    <textarea id={`op-${id}`} rows={rows} required={req} className="field" placeholder={ph} value={f[id]} onChange={set(id)} data-testid={`opp-${id}-input`} />
  </div>
);

const Success = ({ refNo }) => (
  <div className="border border-[rgba(212,175,55,0.35)] p-10 md:p-16 text-center" data-testid="opp-success">
    <CheckCircle2 className="w-12 h-12 text-gold mx-auto" />
    <h2 className="font-display text-4xl mt-5">Thank You for Your Business Inquiry</h2>
    {refNo && <p className="font-mono text-sm text-gold mt-3" data-testid="opp-success-ref">Reference {refNo}</p>}
    <div className="text-slate-300 mt-5 max-w-xl mx-auto space-y-3 leading-relaxed">
      <p>Thank you for contacting Selby Asset Group and sharing your opportunity with us.</p>
      <p>Your submission has been received successfully. Our team will review the information and contact you if we need additional details or would like to discuss the opportunity further.</p>
      <p>We appreciate your interest in working with Selby Asset Group.</p>
    </div>
  </div>
);

export const OpportunityForm = ({ category, setCategory, division = "", scrollTarget = "opportunity-form" }) => {
  const [f, setF] = useState({ ...EMPTY, division });
  const [files, setFiles] = useState([]);
  const [assets, setAssets] = useState([]);
  const [assetsInvalid, setAssetsInvalid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const uploading = files.some((x) => x.state === "uploading");
  const failed = files.some((x) => x.state === "error");

  const submit = async (e) => {
    e.preventDefault();
    if (!category) return toast.error("Please choose an opportunity category.");
    if (!assets.length) {
      setAssetsInvalid(true);
      document.querySelector('[data-testid="asset-multiselect"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return toast.error("Please select at least one asset category.");
    }
    if (uploading) return toast.error("Please wait for your files to finish uploading.");
    if (failed) return toast.error("Remove the files that couldn't be uploaded before submitting.");
    setBusy(true);
    try {
      const { data } = await http.post("/opportunities", { ...f, category, asset_types: assets, files: files.map((x) => ({ id: x.id, token: x.token })) });
      setDone(data?.ref || "");
      document.getElementById(scrollTarget)?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) return <Success refNo={done} />;
  return (
    <form onSubmit={submit} className="space-y-12" data-testid="opp-form">
      <input type="text" tabIndex={-1} autoComplete="off" className="hidden" value={f.website} onChange={set("website")} aria-hidden="true" />
      <fieldset className="grid sm:grid-cols-2 gap-5">
        <legend className="eyebrow mb-5">Contact information</legend>
        <In id="name" label="Full name" req f={f} set={set} />
        <In id="company" label="Business or company name" req f={f} set={set} />
        <In id="email" label="Business email address" req type="email" f={f} set={set} />
        <In id="phone" label="Phone number" req type="tel" f={f} set={set} />
        <In id="website_url" label="Business website" f={f} set={set} ph="https://" />
        <In id="address" label="Business address" f={f} set={set} />
      </fieldset>
      <fieldset className="grid sm:grid-cols-2 gap-5">
        <legend className="eyebrow mb-5">Opportunity details</legend>
        <div>
          <label className="label" htmlFor="op-category">Opportunity category *</label>
          <select id="op-category" required className="field" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="opp-category-select">
            <option value="">Select a category</option>
            {OPP_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <In id="industry" label="Industry or business sector" f={f} set={set} />
        <div className="sm:col-span-2"><AssetMultiSelect value={assets} onChange={(v) => { setAssets(v); if (v.length) setAssetsInvalid(false); }} defaultOpen={division || "Truck Division"} invalid={assetsInvalid} /></div>
        <div className="sm:col-span-2"><In id="subject" label="Proposal, subject or project title" req f={f} set={set} /></div>
        <Area id="description" label="Description of the business, contract, project or partnership opportunity" req rows={6} f={f} set={set} />
        <Area id="requested_action" label="Proposed partnership or service: what would you like Selby Asset Group to do?" req rows={3} f={f} set={set} />
        <In id="value" label="Estimated project value or budget" f={f} set={set} />
        <In id="timeline" label="Proposed timeline or expected start date" f={f} set={set} />
        <Area id="comments" label="Additional comments or requirements" rows={3} f={f} set={set} />
      </fieldset>
      <section>
        <p className="eyebrow mb-3">Upload supporting documents</p>
        <p className="text-sm text-slate-300 mb-5 max-w-2xl">Have a proposal, contract, project specifications, business presentation, or other supporting materials? Attach your documents to help us better understand your opportunity.</p>
        <FileUploader files={files} setFiles={setFiles} />
        <p className="text-xs text-slate-400 mt-3">Documents are stored privately and are only accessible to authorized Selby Asset Group staff.</p>
      </section>
      <div className="flex flex-col sm:flex-row sm:items-center gap-5 border-t hairline pt-8">
        <button className="btn-gold" disabled={busy || uploading} data-testid="opp-submit-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit Opportunity</button>
        <p className="text-xs text-slate-400 max-w-md">Submitting creates no obligation for either party and does not mean a partnership, contract or proposal has been accepted. We review every submission and respond where there is a potential fit.</p>
      </div>
    </form>
  );
};

export default function Opportunities() {
  const [category, setCategory] = useState("");
  const pick = (c) => {
    setCategory(c);
    document.getElementById("opportunity-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <div data-testid="opportunities-page">
      <PageHero eyebrow="Business Opportunities" title={["Let's Explore", "Opportunities Together."]} intro="Partnerships, contracts, projects and collaboration with Selby Asset Group." testid="opportunities-hero" />
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-24 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-4"><Handshake className="w-10 h-10 text-gold" /><p className="eyebrow mt-6">Work with us</p></Reveal>
        <div className="lg:col-span-8 space-y-6">
          {INTRO.map((p, i) => <Reveal key={i} delay={i * 0.04}><p className={i === 0 ? "font-display text-3xl md:text-4xl leading-snug text-slate-100" : "text-base md:text-lg text-slate-300 leading-relaxed"}>{p}</p></Reveal>)}
          <Reveal><p className="border-l-2 border-[var(--gold)] pl-6 font-display text-2xl md:text-3xl text-slate-50">Let's build valuable business relationships together.</p></Reveal>
        </div>
      </section>
      <section className="border-t hairline bg-[var(--ink-2)] py-20" data-testid="opp-categories">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <p className="eyebrow mb-8">Opportunity types we welcome</p>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border hairline">
            {OPP_CATEGORIES.map((c, i) => (
              <li key={c} className="bg-[var(--ink-2)]">
                <button type="button" onClick={() => pick(c)} aria-pressed={category === c} className={`w-full h-full min-h-[120px] text-left p-5 flex flex-col justify-between gap-4 transition-colors ${category === c ? "bg-[rgba(212,175,55,0.1)]" : "hover:bg-white/[0.03]"}`} data-testid={`opp-category-${i}`}>
                  <span className="font-mono text-xs text-gold">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-display text-xl text-slate-50 leading-tight">{c}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </section>
      <section id="opportunity-form" className="scroll-mt-24 max-w-5xl mx-auto px-5 md:px-10 py-20 md:py-28">
        <Reveal className="mb-12"><p className="eyebrow">Submit an opportunity</p><h2 className="font-display text-4xl md:text-5xl mt-4">Tell us about your <em className="text-gold">opportunity.</em></h2></Reveal>
        <OpportunityForm category={category} setCategory={setCategory} />
      </section>
    </div>
  );
}
